import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import initialBalanceService from '../services/initialBalanceService';
import { formatCurrency, formatDate } from '../utils/formatters';

const InitialBalance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});

  const loadBalances = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar saldo atual
      const current = await initialBalanceService.getCurrentBalance();
      setCurrentBalance(current);
      
      // Carregar histórico (se houver endpoint para isso)
      // const history = await initialBalanceService.getAll();
      // setBalances(history);
      
    } catch (error) {
      console.error('Erro ao carregar saldos:', error);
      toast.error('Erro ao carregar saldos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.amount) {
      newErrors.amount = 'O valor é obrigatório';
    } else if (isNaN(parseFloat(formData.amount))) {
      newErrors.amount = 'O valor deve ser um número válido';
    }
    
    if (!formData.date) {
      newErrors.date = 'A data é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const balanceData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      await initialBalanceService.create(balanceData);
      toast.success('Saldo inicial definido com sucesso!');
      
      // Resetar formulário
      setFormData({
        amount: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      // Recarregar dados
      loadBalances();
    } catch (error) {
      console.error('Erro ao salvar saldo inicial:', error);
      toast.error('Erro ao salvar saldo inicial. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container data-testid="initial-balance-page">
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">Saldo Inicial</h5>
          <div className="sheet-toolbar">
            <Button 
              variant="outline-secondary"
              onClick={() => navigate('/dashboard')}
            >
              <FaArrowLeft className="me-1" /> Voltar
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          {/* Saldo Atual */}
          {currentBalance && (
            <div className="mb-4">
              <div className="dashboard-card">
                <div className="card-content">
                  <h6>Saldo Inicial Atual</h6>
                  <h4 className={`${currentBalance.amount >= 0 ? 'text-success' : 'text-danger'}`} data-testid="current-balance">
                    {formatCurrency(currentBalance.amount)}
                  </h4>
                  <small className="text-muted">
                    Definido em {formatDate(currentBalance.date)}
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Formulário */}
          <Form onSubmit={handleSubmit} className="excel-form" data-testid="initial-balance-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Data</Form.Label>
                  <Form.Control
                    type="date"
                    name="date" data-testid="initial-balance-date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.date ? 'is-invalid' : ''}`}
                  />
                  {errors.date && (
                    <div className="invalid-feedback">{errors.date}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Valor</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="amount" data-testid="initial-balance-amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.amount ? 'is-invalid' : ''}`}
                    placeholder="0,00"
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">{errors.amount}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button 
                  type="submit" data-testid="save-button"
                  variant="primary"
                  disabled={loading}
                  className="mb-3"
                >
                  <FaSave className="me-1" /> 
                  {loading ? 'Salvando...' : 'Definir Saldo'}
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Informações */}
          <div className="mt-4">
            <div className="alert alert-info">
              <h6>ℹ️ Como funciona o Saldo Inicial</h6>
              <ul className="mb-0">
                <li>O saldo inicial representa o valor disponível no início do período</li>
                <li>Todas as transações posteriores à data definida serão calculadas a partir deste valor</li>
                <li>Alterar o saldo inicial recalculará automaticamente todos os saldos</li>
                <li>Recomendamos definir o saldo inicial na data mais antiga das suas transações</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default InitialBalance; 